import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useSocketStore } from './socket';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IntercomCall {
  id: string;
  callId?: string;
  sipCallId?: string;
  cameraId: string;
  cameraName: string;
  direction: 'inbound' | 'outbound';
  status: 'ringing' | 'active' | 'ended' | 'missed' | 'rejected';
  fromUri?: string;
  startedAt: string;
  answeredAt?: string;
  doorOpened?: boolean;
}

export interface SIPDevice {
  id: string;
  cameraName: string;
  hostname?: string;
  sipEnabled: boolean;
  sipUsername?: string;
  sipContactAddress?: string;
  sipRegisteredAt?: string;
  sipDoorOpenMethod?: string;
  sipAutoAnswer?: boolean;
  liveStatus?: {
    contactAddress: string;
    registeredAt: string;
    expiresAt: string;
    online: boolean;
  } | null;
}

export interface StreamEndpoint {
  whepUrl: string;
  iceServers: RTCIceServer[];
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useIntercomStore = defineStore('intercom', () => {
  const socketStore = useSocketStore();

  // ── State ──────────────────────────────────────────────────────────────────

  /** Currently ringing or active call */
  const activeCall = ref<IntercomCall | null>(null);

  /** Video stream endpoint received after answering a call */
  const streamEndpoint = ref<StreamEndpoint | null>(null);

  /** Registered SIP devices */
  const sipDevices = ref<SIPDevice[]>([]);
  const devicesLoading = ref(false);

  const isAnsweringCall = ref(false);
  const doorOpening = ref(false);
  const error = ref<string | null>(null);

  // ── Computed ───────────────────────────────────────────────────────────────

  const hasIncomingCall = computed(
    () => activeCall.value?.status === 'ringing' && activeCall.value.direction === 'inbound',
  );

  const isInCall = computed(() => activeCall.value?.status === 'active');

  /** Outbound call that is still ringing (doorphone not answered yet) */
  const isCalling = computed(() => activeCall.value?.status === 'ringing' && activeCall.value.direction === 'outbound');

  const onlineDevices = computed(() => sipDevices.value.filter((d) => d.liveStatus?.online));

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Load active/ringing calls (in case we missed a socket event) */
  async function loadActiveCalls() {
    try {
      const calls: IntercomCall[] = await apiFetch('/intercom/calls/active');
      if (calls.length > 0 && !activeCall.value) {
        // Pick the most recent ringing call
        const ringing = calls.find((c) => c.status === 'ringing') || calls[0];
        // API uses callId; normalize to id for answer/reject calls
        activeCall.value = {
          ...ringing,
          id: ringing.id || ringing.callId || '',
        };
      }
    } catch (e) {
      console.error('[IntercomStore] Error loading active calls:', e);
    }
  }

  /** Load SIP devices */
  async function loadSIPDevices() {
    devicesLoading.value = true;
    try {
      const data: SIPDevice[] = await apiFetch('/intercom/devices');
      sipDevices.value = data;
    } catch (e) {
      console.error('[IntercomStore] Error loading SIP devices:', e);
    } finally {
      devicesLoading.value = false;
    }
  }

  /** Answer an incoming ringing call */
  async function answerCall(callId: string) {
    isAnsweringCall.value = true;
    error.value = null;
    try {
      const data = await apiFetch(`/intercom/calls/${callId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      streamEndpoint.value = data.streamEndpoint;
      if (activeCall.value?.id === callId) {
        activeCall.value.status = 'active';
        activeCall.value.answeredAt = new Date().toISOString();
      }
    } catch (__caught: unknown) {
      const e = __caught as Error;
      error.value = e?.message || tt('Failed to answer call', 'Klica ni bilo mogoče sprejeti');
      throw e;
    } finally {
      isAnsweringCall.value = false;
    }
  }

  /** Reject an incoming ringing call */
  async function rejectCall(callId: string) {
    try {
      await apiFetch(`/intercom/calls/${callId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (activeCall.value?.id === callId) {
        clearActiveCall();
      }
    } catch (e) {
      console.error('[IntercomStore] Error rejecting call:', e);
    }
  }

  /** Hang up an active call */
  async function hangupCall(callId: string) {
    try {
      await apiFetch(`/intercom/calls/${callId}/hangup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (activeCall.value?.id === callId) {
        clearActiveCall();
      }
    } catch (e) {
      console.error('[IntercomStore] Error hanging up:', e);
    }
  }

  /** Open the door during an active call */
  async function openDoor(callId: string) {
    doorOpening.value = true;
    try {
      const data = await apiFetch(`/intercom/calls/${callId}/door-open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (activeCall.value?.id === callId && data.success) {
        activeCall.value.doorOpened = true;
      }
      return data.success as boolean;
    } catch (e) {
      console.error('[IntercomStore] Error opening door:', e);
      return false;
    } finally {
      setTimeout(() => {
        doorOpening.value = false;
      }, 2000);
    }
  }

  /** Initiate an outbound call to a doorphone */
  async function callDevice(cameraId: string): Promise<IntercomCall | null> {
    error.value = null;
    try {
      const data = await apiFetch(`/intercom/cameras/${cameraId}/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const call: IntercomCall = {
        id: data.callId,
        sipCallId: data.sipCallId,
        cameraId,
        cameraName: sipDevices.value.find((d) => d.id === cameraId)?.cameraName || 'Camera',
        direction: 'outbound',
        status: data.status || 'ringing',
        startedAt: new Date().toISOString(),
      };
      activeCall.value = call;
      return call;
    } catch (__caught: unknown) {
      const e = __caught as Error;
      error.value = e?.message || tt('Failed to initiate call', 'Klica ni bilo mogoče vzpostaviti');
      return null;
    }
  }

  function clearActiveCall() {
    activeCall.value = null;
    streamEndpoint.value = null;
  }

  // ── Socket.IO Event Listeners ──────────────────────────────────────────────

  function setupListeners() {
    const socket = socketStore.socket;
    if (!socket) return;

    socket.on('intercom:incoming_call', (data: IntercomCall) => {
      console.log('[IntercomStore] Incoming call:', data);
      activeCall.value = {
        ...data,
        // Socket payload uses callId; normalize to id for answer/reject calls
        id: data.id || data.callId || '',
        status: 'ringing',
      };
      // Vibrate/audio notification if the browser supports it
      try {
        window.navigator.vibrate?.([200, 100, 200]);
      } catch {
        /* vibration is not available everywhere */
      }
    });

    socket.on('intercom:call_answered', (data: { callId: string; userId: string }) => {
      if (activeCall.value?.id === data.callId) {
        activeCall.value.status = 'active';
        activeCall.value.answeredAt = new Date().toISOString();
      }
    });

    socket.on('intercom:call_ended', (data: { callId: string; reason: string }) => {
      if (activeCall.value?.id === data.callId) {
        const wasOutboundRinging = activeCall.value.direction === 'outbound' && activeCall.value.status === 'ringing';
        clearActiveCall();
        // Surface rejections (603 Decline, 486 Busy, ...) so the user
        // sees why an outbound call did not connect
        if (wasOutboundRinging && data.reason && /^\d{3}$/.test(String(data.reason))) {
          error.value = tt(`Doorphone declined the call (${data.reason})`, `Domofon je klic zavrnil (${data.reason})`);
        }
      }
    });

    socket.on('intercom:call_missed', (data: { callId: string }) => {
      if (activeCall.value?.id === data.callId) {
        clearActiveCall();
      }
    });

    socket.on('intercom:door_opened', (data: { callId: string }) => {
      if (activeCall.value?.id === data.callId) {
        activeCall.value.doorOpened = true;
      }
    });

    socket.on('intercom:device_registered', () => {
      loadSIPDevices();
    });

    socket.on('intercom:device_unregistered', () => {
      loadSIPDevices();
    });
  }

  /** Initialize the store — load data + subscribe to socket events */
  async function initialize() {
    setupListeners();
    await Promise.all([loadSIPDevices(), loadActiveCalls()]);
  }

  return {
    // State
    activeCall,
    streamEndpoint,
    sipDevices,
    isAnsweringCall,
    doorOpening,
    error,
    // Computed
    hasIncomingCall,
    isInCall,
    isCalling,
    onlineDevices,
    // Actions
    initialize,
    loadSIPDevices,
    answerCall,
    rejectCall,
    hangupCall,
    openDoor,
    callDevice,
  };
});
